// Conditional-visibility helpers shared by every questionnaire, parameterised by the flow's
// question id field and question order field (see createQuestionVisibility below).
// Semantics must stay in lockstep with backend/Services/ConditionalQuestionRules.cs:
// a question with a parentQuestionId is visible only when its parent is visible and the
// parent's answer matches one of the question's triggerAnswers (comma-separated,
// case-insensitive; null/blank defaults to "Yes"). Questions with no parent, an unknown
// parent, or a parent cycle are visible (fail open). The server replaces now-hidden answers
// with derived parent-inherited answers where applicable (No -> No,
// Not Applicable -> Not Applicable, and No under a scope gate -> Not Applicable).

const DEFAULT_TRIGGER = 'Yes';

function answerTriggers(triggerAnswers, parentAnswer) {
    if (!parentAnswer || !String(parentAnswer).trim()) return false;
    const triggers = triggerAnswers && String(triggerAnswers).trim() ? triggerAnswers : DEFAULT_TRIGGER;
    const answer = String(parentAnswer).trim().toLowerCase();
    return String(triggers)
        .split(',')
        .map(t => t.trim().toLowerCase())
        .some(t => t.length > 0 && t === answer);
}

// idField: the flow's question id property (e.g. 'organizationQuestionId').
// orderField: the property that sorts a parent's children (e.g. 'questionOrder').
export function createQuestionVisibility({ idField, orderField } = {}) {
    if (!idField) throw new Error('createQuestionVisibility: idField is required');
    if (!orderField) throw new Error('createQuestionVisibility: orderField is required');

    function buildQuestionMap(questions) {
        return new Map((questions || []).map(q => [q[idField], q]));
    }

    function isQuestionVisible(question, byId, responses) {
        if (!question) return false;

        // Walk up the parent chain collecting conditional links; cycles fail open (visible).
        const chain = [];
        const seen = new Set();
        let current = question;
        while (current && current.parentQuestionId != null) {
            if (seen.has(current[idField])) return true;
            seen.add(current[idField]);
            const parent = byId.get(current.parentQuestionId);
            if (!parent) break; // unknown parent: treat as unconditional
            chain.push(current);
            current = parent;
        }

        // Evaluate top-down: every link's parent answer must match that link's triggers.
        for (let i = chain.length - 1; i >= 0; i--) {
            const child = chain[i];
            const parentAnswer = responses[String(child.parentQuestionId)];
            if (!answerTriggers(child.triggerAnswers, parentAnswer)) return false;
        }

        return true;
    }

    // Roots keep their incoming (API) order; each parent's children are spliced immediately
    // after the parent, recursively, sorted by orderField. Orphan children (parent not
    // in the list) are treated as roots. Cycle members unreachable from a root are appended
    // at the end so no question is ever dropped.
    function orderQuestionsWithChildren(questions) {
        const list = questions || [];
        const byId = buildQuestionMap(list);
        const childrenByParent = new Map();
        const roots = [];

        for (const q of list) {
            const pid = q.parentQuestionId;
            if (pid != null && pid !== q[idField] && byId.has(pid)) {
                if (!childrenByParent.has(pid)) childrenByParent.set(pid, []);
                childrenByParent.get(pid).push(q);
            } else {
                roots.push(q);
            }
        }

        const result = [];
        const visited = new Set();
        const append = (q) => {
            if (visited.has(q[idField])) return;
            visited.add(q[idField]);
            result.push(q);
            const kids = (childrenByParent.get(q[idField]) || [])
                .slice()
                .sort((a, b) => (a[orderField] || 0) - (b[orderField] || 0));
            kids.forEach(append);
        };

        roots.forEach(append);
        list.forEach(q => { if (!visited.has(q[idField])) append(q); });
        return result;
    }

    // A conditional follow-up is shown in its top-level ancestor's section, whatever its own
    // category, so the Sections sidebar and the question card keep a gate and its follow-ups together.
    // Walks up to the highest ancestor present in the list (an unknown parent ends the walk; a cycle
    // keeps the question's own category). Display only: the question's riskDomain is not changed.
    function inheritRootCategory(questions, categoryField) {
        const list = questions || [];
        const byId = buildQuestionMap(list);
        return list.map(q => {
            let root = q;
            const seen = new Set([q[idField]]);
            while (root.parentQuestionId != null && byId.has(root.parentQuestionId)) {
                if (seen.has(root.parentQuestionId)) return q;
                seen.add(root.parentQuestionId);
                root = byId.get(root.parentQuestionId);
            }
            return root === q || root[categoryField] === q[categoryField]
                ? q
                : { ...q, [categoryField]: root[categoryField] };
        });
    }

    function getVisibleQuestions(orderedQuestions, responses) {
        const byId = buildQuestionMap(orderedQuestions);
        return (orderedQuestions || []).filter(q => isQuestionVisible(q, byId, responses || {}));
    }

    // Descendant ids whose question-scoped state (answer, attachments, notes, dirty flag) must be
    // cleared when `questionId`'s answer changes: any descendant not visible both before and after
    // the change. Despite the name it returns ids, so callers can clear every kind of state. Now-hidden answers are discarded (the server
    // replaces them with derived parent-inherited answers where applicable); newly revealed
    // questions must be re-answered because their stored answer was derived or stale.
    function collectDescendantAnswersToClear(questionId, questions, prevResponses, nextResponses) {
        const byId = buildQuestionMap(questions);
        return collectDescendantIds(questionId, questions).filter(dId => {
            const dStr = String(dId);
            if (prevResponses[dStr] === undefined && nextResponses[dStr] === undefined) return false;
            const q = byId.get(dId);
            const visibleBefore = isQuestionVisible(q, byId, prevResponses);
            const visibleAfter = isQuestionVisible(q, byId, nextResponses);
            return !(visibleBefore && visibleAfter);
        });
    }

    // All transitive children of a question (for clearing answers when a parent flips).
    function collectDescendantIds(questionId, questions) {
        const childrenByParent = new Map();
        for (const q of questions || []) {
            if (q.parentQuestionId != null) {
                if (!childrenByParent.has(q.parentQuestionId)) childrenByParent.set(q.parentQuestionId, []);
                childrenByParent.get(q.parentQuestionId).push(q[idField]);
            }
        }

        const result = [];
        const seen = new Set();
        const queue = [...(childrenByParent.get(questionId) || [])];
        while (queue.length > 0) {
            const id = queue.shift();
            if (seen.has(id)) continue;
            seen.add(id);
            result.push(id);
            queue.push(...(childrenByParent.get(id) || []));
        }
        return result;
    }

    return {
        buildQuestionMap,
        isQuestionVisible,
        orderQuestionsWithChildren,
        inheritRootCategory,
        getVisibleQuestions,
        collectDescendantAnswersToClear,
        collectDescendantIds,
    };
}
